import { entity } from 'simpler-state';
import { GlobalFilterState, customer_source } from '@/global_filter';

/**
 * Document Processing Metrics
 * Tracks the volume and processing of incoming documents
 */
export interface DocumentProcessingMetrics {
  /** Number of deduction documents received */
  deductions_received: number;
  /** Number of invoice documents received */
  invoices_received: number;
  /** Number of backup lines extracted from documents */
  lines_extracted: number;
  /** Number of documents that have been categorized */
  categorizations: number;
  // TODO: Re-enable when needed
  // pdfs_parsed: number;
}

/**
 * Task Management Metrics
 * Tracks task assignment, completion, and workflow progression
 */
export interface TaskManagementMetrics {
  /** Total number of tasks assigned */
  tasks_assigned: number;
  /** Number of status changes (workflow progression) */
  stages_changed: number;
  /** Number of tasks that have been completed */
  tasks_completed: number;
}

/**
 * Disputes & Backups Metrics
 * Tracks dispute filing, backup requests, and resolution outcomes
 */
export interface DisputesBackupsMetrics {
  /** Number of backup requests filed */
  backups_requested: number;
  /** Number of disputes filed */
  disputes_filed: number;
  /** Number of backup requests that have been fulfilled */
  backups_received: number;
  /** Number of disputes that were won */
  disputes_won: number;
}

/**
 * Financial Processing Metrics
 * Tracks validation and accounting mapping activities
 */
export interface FinancialProcessingMetrics {
  /** Number of deductions that have been validated */
  deductions_validated: number;
  /** Number of GL mappings created */
  gl_mappings_made: number;
}

export interface AnalyticsData {
  document_processing: DocumentProcessingMetrics;
  task_management: TaskManagementMetrics;
  disputes_backups: DisputesBackupsMetrics;
  financial_processing: FinancialProcessingMetrics;
}

interface AnalyticsState {
  data: AnalyticsData | null;
  isLoading: boolean;
  error: string | null;
}

export const analyticsState = entity<AnalyticsState>({
  data: null,
  isLoading: false,
  error: null
});

/**
 * The `/analytics` query. It takes a `deduction.source`, so an Other name (`other:<name>`) from the
 * customer filter is sent as `other` — sent as is, the endpoint answered 400 and the dashboard showed
 * "Error loading analytics".
 */
export function analytics_params(startDate?: Date, endDate?: Date, source?: string | null): URLSearchParams {
  const params = new URLSearchParams();
  if (startDate) {
    params.append('start_date', startDate.toISOString());
  }
  if (endDate) {
    params.append('end_date', endDate.toISOString());
  }
  if (source) {
    params.append('source', customer_source(source));
  }
  return params;
}

export const fetchAnalytics = async (startDate?: Date, endDate?: Date, source?: string | null) => {
  try {
    analyticsState.set(state => ({ ...state, data: null, isLoading: true, error: null }));
    
    const params = analytics_params(startDate, endDate, source);
    
    const url = `/api/analytics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) throw new Error('Failed to fetch analytics');
    
    const data: AnalyticsData = await response.json();

    analyticsState.set(state => ({ 
      ...state,
      data, 
      isLoading: false,
      error: null 
    }));
  } catch (err) {
    analyticsState.set(state => ({ 
      ...state, 
      error: (err as Error).message, 
      isLoading: false 
    }));
  }
};

if (import.meta.vitest) {
  describe("analytics_params", () => {
    it("sends an Other name as source other, the only Other the endpoint knows", () => {
      expect(analytics_params(undefined, undefined, "other:Eight Point").get("source")).toBe("other")
      expect(analytics_params(undefined, undefined, "kehe").get("source")).toBe("kehe")
      expect(analytics_params(undefined, undefined, null).has("source")).toBe(false)
    })
  })
}
